import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CContainer, 
  CButton, 
  CSpinner, 
  CBadge,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import api from '../../api/axiosInstance';

import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// Helper Function: HTML entities clean aur KaTeX latex rendering
const RenderMathText = ({ rawContent }) => {
  if (!rawContent) return null;

  let cleanText = rawContent
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]*>?/gm, '');

  const parts = cleanText.split(/(\$\$[\s\S]*?\$\$|\$[^$]*\$)/g);

  return (
    <div style={{ 
      fontSize: '1.05rem', 
      lineHeight: '1.7', 
      color: '#1f2937', 
      wordBreak: 'break-word', 
      overflowWrap: 'anywhere' 
    }}>
      {parts.map((part, index) => {
        if (!part) return null;

        if (part.startsWith('$$') && part.endsWith('$$')) {
          const math = part.slice(2, -2).replace(/&nbsp;/gi, ' ');
          return (
            <div key={index} style={{ overflowX: 'auto', maxWidth: '100%', margin: '8px 0' }}>
              <BlockMath math={math} />
            </div>
          );
        } else if (part.startsWith('$') && part.endsWith('$')) {
          const math = part.slice(1, -1).replace(/&nbsp;/gi, ' ');
          return <InlineMath key={index} math={math} />;
        }
        return <span key={index}>{part}</span>;
      })}
    </div>
  );
};

const TopicQuestionsList = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { section, title } = params;

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: 'auth' });

  // Auth Details
  const authToken = localStorage.getItem('authToken');
  const memberId = localStorage.getItem('memberId') || localStorage.getItem('ID');

  // Dynamic Access State
  const [hasAccess, setHasAccess] = useState(false);

  const getTopicFromUrl = () => {
    if (params.topicSlug) return params.topicSlug;
    if (params.topic) return params.topic;
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || '';
  };

  const topicNameRaw = getTopicFromUrl();
  const cleanTitle = title ? title.replace(/-/g, ' ') : '';
  const [chapterName, setChapterName] = useState('');
  const cleanTopicName = topicNameRaw ? topicNameRaw.replace(/-/g, ' ') : '';

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // PAGE LOAD CHECK
  useEffect(() => {
    window.scrollTo(0, 0);
    initializePage();
  }, [section, title, topicNameRaw]);

  const initializePage = async () => {
    // 1. LOGIN CHECK
    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required 🔒',
        message: 'You need to log in to view the questions on this page.',
        type: 'auth'
      });
      setModalVisible(true);
      setLoading(false);
      return;
    }

    // 2. FETCH CATEGORY ID & VERIFY SUBSCRIPTION ACCESS
    let catId = null;
    try {
      const catRes = await api.get(`/api/categories/GetBySlug?slug=${title}`, { skipAuthCheck: true });
      if (catRes.data) {
        catId = catRes.data.id || catRes.data.Id || catRes.data.ID;
      }
    } catch (e) {
      console.warn("GetBySlug failed in Questions page:", e);
    }

    // Fallback if GetBySlug fails
    if (!catId) {
      try {
        const allCatRes = await api.get('/api/Admin/GetAllCategories', { skipAuthCheck: true });
        const categories = allCatRes.data || [];
        const matched = categories.find(
          c => (c.slug && c.slug.toLowerCase() === title.toLowerCase()) || 
               (c.title && c.title.toLowerCase().trim() === cleanTitle.toLowerCase().trim())
        );
        if (matched) catId = matched.id || matched.Id;
      } catch (e) {
        console.error("All categories fetch failed:", e);
      }
    }

    const accessGranted = await checkCategoryAccess(catId);
    setHasAccess(accessGranted);

    if (!accessGranted) {
      setModalConfig({
        title: 'Subscription Plan Required 🔒',
        message: 'To access the questions and solutions on this page, you need an active subscription plan that covers this category.',
        type: 'upgrade'
      });
      setModalVisible(true);
      setLoading(false);
      return;
    }

    // 3. FETCH QUESTIONS IF ACCESS GRANTED
    if (cleanTopicName) {
      fetchTopicQuestions();
    } else {
      setError("Invalid Topic Parameter.");
      setLoading(false);
    }
  };

 const checkCategoryAccess = async (catId) => {
    try {
      // 1. LocalStorage se activeTiers check karein
      const activeTiers = JSON.parse(localStorage.getItem('activeTiers') || '[]');
      const hasSigmaTier = activeTiers.some(
        (tier) => String(tier).trim().toLowerCase() === 'sigma'
      );

      // Agar Sigma active hai toh true return karein (MISSING RETURN FIXED HERE)
      if (hasSigmaTier) {
        return true;
      }

      // 2. LocalStorage se allowedCategoryIds array fetch karein
      const storedCategoryIds = JSON.parse(localStorage.getItem('allowedCategoryIds') || '[]');
      
      if (catId && storedCategoryIds.map(id => id.toString().trim()).includes(catId.toString().trim())) {
        return true;
      }

      // 3. API Fallback Check (Agar user direct URL se aaya ho)
      if (memberId) {
        const subRes = await api.get(`/api/members/${memberId}/subscription`);
        const userSubscriptions = Array.isArray(subRes.data) ? subRes.data : [subRes.data];

        for (const sub of userSubscriptions) {
          if (!sub) continue;

          // Tier Name ya Section Name me Sigma match karein
          const tierName = String(sub.tierName || sub.sectionName || '').toLowerCase();
          if (tierName.includes('sigma')) {
            return true;
          }

          // Category IDs Check
          if (sub.categoryIds) {
            const allowedCategoryArray = sub.categoryIds.split(',').map(id => id.trim());
            if (catId && allowedCategoryArray.includes(catId.toString().trim())) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (err) {
      console.error("Access verification error:", err);
      return false;
    }
  };

  const fetchTopicQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = `/api/content/GetTopicQuestions?section=${encodeURIComponent(section)}&category=${encodeURIComponent(cleanTitle)}&topic=${encodeURIComponent(cleanTopicName)}`;
      const res = await api.get(endpoint);

      if (res.data) {
        const fetchedQuestions = res.data.questions || [];
        setQuestions(fetchedQuestions);
        
        const extractedChapter = res.data.chapterName || fetchedQuestions[0]?.chapterName || '';
        setChapterName(extractedChapter);
      }
    } catch (err) {
      console.error("Error fetching topic questions:", err);
      setError("Failed to load questions for this topic.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetSolution = (q) => {
    const rawQuestionText = q.customUrl || q.customeUrl || q.questionText || q.QuestionText || '';
    const truncatedText = getTruncatedText(rawQuestionText, 50);
    const slug = createCleanSlug(truncatedText);
    const targetUrl = `/content/view-question/${q.id}/${slug}`;

    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required 🔒',
        message: 'Login is necessary to view the solution.',
        type: 'auth'
      });
      setModalVisible(true);
      return;
    }

    if (!hasAccess) {
      setModalConfig({
        title: 'Subscription Required 🔒',
        message: 'Ye solution unlock karne ke liye aapke paas is category ka active Subscription Plan hona chahiye.',
        type: 'upgrade'
      });
      setModalVisible(true);
      return;
    }

    navigate(targetUrl);
  };

  const handleModalConfirm = (e) => {
    if (e) e.preventDefault();

    if (!authToken || modalConfig.type === 'auth') {
      navigate('/login', { state: { from: location.pathname } });
    } else {
      navigate('/profile');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    navigate(-1);
  };

  const getTruncatedText = (htmlContent, maxLength = 50) => {
    if (!htmlContent) return '';
    const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
    const plainText = doc.body.textContent || "";
    return plainText.trim().substring(0, maxLength);
  };

  const createCleanSlug = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    return rawUrl
      .replace(/<[^>]*>/g, '')
      .replace(/%C2%A0/g, '-')
      .replace(/%20/g, '-')
      .replace(/[\/\\]/g, '-')
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Questions & Formulas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      
      {/* TOP HEADER BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <span className="text-warning fw-bold text-uppercase d-block mb-1" style={{ fontSize: '13px', letterSpacing: '1px' }}>
            {section?.toUpperCase()} • {cleanTitle.toUpperCase()}
          </span>
          <h1 className="fw-bold m-0 mt-1 display-6 text-capitalize" style={{ color: 'yellow' }}>
            {chapterName ? `${chapterName} - ` : ''}{cleanTopicName}
          </h1>
          <p className="text-light opacity-90 m-0 small mt-2">
            Questions & Solutions Repository
          </p>
          <div className="mt-3">
            <CBadge color="danger" className="px-3 py-2 fs-6 rounded-pill fw-bold">
              {questions.length} Questions Available
            </CBadge>
          </div>
        </div>
      </div>

      {/* QUESTIONS LIST CONTAINER */}
      <CContainer className="pb-5 px-3 px-md-4" style={{ maxWidth: '1200px' }}>
        {questions.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {questions.map((q, index) => {
              const isLocked = !hasAccess;

              return (
                <div 
                  key={q.id || index}
                  className="bg-white p-3 p-md-4 rounded-3 border shadow-sm question-card w-100"
                  style={{ transition: 'all 0.2s ease-in-out', cursor: 'pointer', overflow: 'hidden' }}
                  onClick={() => handleGetSolution(q)}
                >
                  <div className="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-3">
                    
                    {/* Number Badge & Dynamic Lock */}
                    <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                      <div 
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm"
                        style={{ width: '28px', height: '28px', fontSize: '0.85rem', flexShrink: 0 }}
                      >
                        {index + 1}
                      </div>
                      <span style={{ fontSize: '0.9rem', opacity: '0.8' }} title={isLocked ? "Subscription Required" : "Unlocked"}>
                        {isLocked ? "🔒" : "🔓"}
                      </span>
                    </div>

                    {/* Question Statement */}
                    <div className="flex-grow-1 w-100" style={{ minWidth: 0, overflowWrap: 'break-word' }}>
                      <RenderMathText rawContent={q.questionText} />
                    </div>

                    {/* Exam Tag */}
                    <div className="align-self-start text-sm-end" style={{ flexShrink: 0 }}>
                      <span 
                        className="fw-bold text-danger d-inline-block text-uppercase border border-danger-subtle bg-light rounded px-2 py-1"
                        style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', letterSpacing: '0.3px' }}
                      >
                        {cleanTitle} {q.year || '2025'}
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !modalVisible && <div className="alert alert-warning text-center">No questions available for this topic.</div>
        )}
      </CContainer>

      {/* COREUI MODAL FOR PAGE ACCESS & SUBSCRIPTION CHECK */}
      <CModal visible={modalVisible} onClose={handleModalClose} alignment="center" backdrop="static">
        <CModalHeader className="bg-dark text-white">
          <CModalTitle className="fw-bold fs-5">{modalConfig.title}</CModalTitle>
        </CModalHeader>
        <CModalBody className="py-4">
          <p className="fs-6 text-dark m-0">{modalConfig.message}</p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleModalClose}>
            Go Back
          </CButton>
          <CButton 
            color={modalConfig.type === 'upgrade' ? "success" : "primary"} 
            className="text-white fw-bold" 
            onClick={handleModalConfirm}
          >
            {modalConfig.type === 'upgrade' ? "Upgrade Plan Now" : "Login Now"}
          </CButton>
        </CModalFooter>
      </CModal>

      <style>{`
        .question-card:hover {
          border-color: #0A2540 !important;
          box-shadow: 0 6px 16px rgba(10, 37, 64, 0.1) !important;
          transform: translateY(-2px);
        }
        .katex {
          font-size: 1.05em;
        }
      `}</style>

    </div>
  );
};

export default TopicQuestionsList;